import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import {
  getSettings,
  updateSettings,
  getOllamaModels,
  getLLMStatus,
  getDatabaseStats,
  getBackups,
  createBackup,
  restoreBackup,
  deleteBackup,
  browseFilesystem,
  getAdminUsersStats,
  forcePasswordChange,
  deleteUser,
  approveUser,
  rejectUser,
  type Settings as SettingsType,
  type OllamaModel,
  type DatabaseStats,
  type BackupFile,
  type DirectoryEntry,
  type UserWithStats,
} from '../api/settings';

type Tab = 'llm' | 'integrations' | 'database' | 'privacy' | 'users';

export function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('llm');
  const [, setSettings] = useState<SettingsType | null>(null);
  const [ollamaModels, setOllamaModels] = useState<OllamaModel[]>([]);
  const [llmStatus, setLlmStatus] = useState<string>('checking');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [provider, setProvider] = useState('ollama');
  const [model, setModel] = useState('');
  const [ollamaUrl, setOllamaUrl] = useState('http://localhost:11434/v1');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [autoRouteImages, setAutoRouteImages] = useState(false);
  const [autoRouteDocuments, setAutoRouteDocuments] = useState(false);

  // Strava integration state
  const [stravaClientId, setStravaClientId] = useState('');
  const [stravaClientSecret, setStravaClientSecret] = useState('');

  // Database state
  const [dbStats, setDbStats] = useState<DatabaseStats | null>(null);
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [backupPath, setBackupPath] = useState('');
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Filesystem browser state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [browserPath, setBrowserPath] = useState('');
  const [browserDirs, setBrowserDirs] = useState<DirectoryEntry[]>([]);
  const [browserParent, setBrowserParent] = useState('');
  const [browserMode, setBrowserMode] = useState<'backup' | 'restore'>('backup');

  // Restore modal state
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreFilePath, setRestoreFilePath] = useState('');

  // Users state
  const [usersData, setUsersData] = useState<UserWithStats[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (provider === 'ollama') {
      loadOllamaModels();
    }
  }, [provider, ollamaUrl]);

  useEffect(() => {
    if (activeTab === 'database') {
      loadDatabaseInfo();
    } else if (activeTab === 'users') {
      loadUsersData();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, statusRes] = await Promise.all([
        getSettings(),
        getLLMStatus(),
      ]);

      if (settingsRes.success) {
        const s = settingsRes.settings;
        setSettings(s);
        setProvider(s.llm_provider || 'ollama');
        setModel(s.llm_model || '');
        setOllamaUrl(s.ollama_base_url || 'http://localhost:11434/v1');
        setAutoRouteImages(s.auto_route_images === 'true');
        setAutoRouteDocuments(s.auto_route_documents === 'true');
        setAnthropicKey(s.anthropic_api_key || '');
        setOpenaiKey(s.openai_api_key || '');
        setStravaClientId(s.strava_client_id || '');
        setStravaClientSecret(s.strava_client_secret ? '••••••••' : '');
      }

      if (statusRes.success) {
        setLlmStatus(statusRes.status);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Errore nel caricamento delle impostazioni' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadOllamaModels = async () => {
    try {
      const res = await getOllamaModels();
      if (res.success && res.models) {
        setOllamaModels(res.models);
        setLlmStatus('connected');
      }
    } catch {
      setOllamaModels([]);
      setLlmStatus('disconnected');
    }
  };

  const loadDatabaseInfo = async () => {
    try {
      const [statsRes, backupsRes] = await Promise.all([
        getDatabaseStats(),
        getBackups(),
      ]);

      if (statsRes.success) {
        setDbStats(statsRes.stats);
        setBackupPath(statsRes.stats.backupPath);
      }

      if (backupsRes.success) {
        setBackups(backupsRes.backups);
      }
    } catch (error) {
      console.error('Error loading database info:', error);
    }
  };

  const loadUsersData = async () => {
    setIsLoadingUsers(true);
    try {
      const res = await getAdminUsersStats();
      if (res.success) {
        setUsersData(res.users);
      }
    } catch (error) {
      console.error('Error loading users data:', error);
      setMessage({ type: 'error', text: 'Errore nel caricamento degli utenti' });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleForcePasswordChange = async (userId: string, userName: string) => {
    if (!confirm(`Forzare il cambio password per "${userName}" al prossimo login?`)) {
      return;
    }

    try {
      const res = await forcePasswordChange(userId);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Cambio password richiesto' });
        await loadUsersData();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nella richiesta',
      });
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Sei sicuro di voler eliminare l'utente "${userName}"?\n\nQuesta azione eliminerà TUTTI i dati associati:\n- Documenti\n- Conversazioni\n- Parametri di laboratorio\n- File caricati\n\nL'operazione è IRREVERSIBILE.`)) {
      return;
    }

    try {
      const res = await deleteUser(userId);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Utente eliminato' });
        await loadUsersData();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nell\'eliminazione',
      });
    }
  };

  const handleApproveUser = async (userId: string, userName: string) => {
    try {
      const res = await approveUser(userId);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || `Utente ${userName} approvato` });
        await loadUsersData();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nell\'approvazione',
      });
    }
  };

  const handleRejectUser = async (userId: string, userName: string) => {
    if (!confirm(`Sei sicuro di voler rifiutare la registrazione di "${userName}"?\n\nL'utente non potrà accedere all'applicazione.`)) {
      return;
    }

    try {
      const res = await rejectUser(userId);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || `Utente ${userName} rifiutato` });
        await loadUsersData();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nel rifiuto',
      });
    }
  };

  const handleCreateBackup = async () => {
    setIsBackingUp(true);
    setMessage(null);

    try {
      const res = await createBackup(backupPath);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Backup creato con successo' });
        await loadDatabaseInfo();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nella creazione del backup',
      });
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreBackup = async (backup: BackupFile) => {
    if (!confirm(`Sei sicuro di voler ripristinare il backup "${backup.name}"? I dati attuali verranno sovrascritti.`)) {
      return;
    }

    setIsRestoring(true);
    setMessage(null);

    try {
      const res = await restoreBackup(backup.path);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Database ripristinato con successo' });
        await loadDatabaseInfo();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nel ripristino del backup',
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDeleteBackup = async (backup: BackupFile) => {
    if (!confirm(`Sei sicuro di voler eliminare il backup "${backup.name}"?`)) {
      return;
    }

    try {
      const res = await deleteBackup(backup.name);
      if (res.success) {
        setMessage({ type: 'success', text: 'Backup eliminato' });
        await loadDatabaseInfo();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nell\'eliminazione del backup',
      });
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openFileBrowser = async (initialPath?: string, mode: 'backup' | 'restore' = 'backup') => {
    try {
      const res = await browseFilesystem(initialPath || backupPath || undefined);
      if (res.success) {
        setBrowserPath(res.currentPath);
        setBrowserDirs(res.directories);
        setBrowserParent(res.parentPath);
        setBrowserMode(mode);
        setShowFileBrowser(true);
      }
    } catch (error) {
      console.error('Error opening file browser:', error);
    }
  };

  const navigateToDir = async (dirPath: string) => {
    try {
      const res = await browseFilesystem(dirPath);
      if (res.success) {
        setBrowserPath(res.currentPath);
        setBrowserDirs(res.directories);
        setBrowserParent(res.parentPath);
      }
    } catch (error) {
      console.error('Error navigating:', error);
    }
  };

  const selectBrowserPath = () => {
    if (browserMode === 'backup') {
      setBackupPath(browserPath);
      setShowFileBrowser(false);
    } else {
      setRestoreFilePath(browserPath);
      setShowFileBrowser(false);
      setShowRestoreModal(true);
    }
  };

  const openRestoreModal = () => {
    // Default to most recent backup path, or the backup directory
    const defaultPath = backups.length > 0 ? backups[0].path : backupPath;
    setRestoreFilePath(defaultPath);
    setShowRestoreModal(true);
  };

  const handleRestoreFromFile = () => {
    if (!restoreFilePath) return;
    setShowRestoreModal(false);
    handleRestoreBackup({
      name: restoreFilePath.split('/').pop() || 'backup',
      path: restoreFilePath,
      size: '',
      sizeBytes: 0,
      createdAt: '',
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const updates: Record<string, string> = {
        llm_provider: provider,
        llm_model: model,
        ollama_base_url: ollamaUrl,
        auto_route_images: autoRouteImages ? 'true' : 'false',
        auto_route_documents: autoRouteDocuments ? 'true' : 'false',
      };

      if (anthropicKey && !anthropicKey.includes('...')) {
        updates.anthropic_api_key = anthropicKey;
      }
      if (openaiKey && !openaiKey.includes('...')) {
        updates.openai_api_key = openaiKey;
      }
      if (stravaClientId) {
        updates.strava_client_id = stravaClientId;
      }
      if (stravaClientSecret && !stravaClientSecret.includes('•')) {
        updates.strava_client_secret = stravaClientSecret;
      }

      const res = await updateSettings(updates);
      if (res.success) {
        setMessage({ type: 'success', text: 'Impostazioni salvate con successo' });
        // Reload to get updated masked values
        await loadSettings();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nel salvataggio',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <Layout title="Accesso Negato">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-3V8m0 0V6m0 2h2m-2 0H9" />
            </svg>
          </div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">Accesso negato</h2>
          <p className="text-base lg:text-lg text-gray-500 mb-6">Solo gli amministratori possono accedere alle impostazioni.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 lg:px-8 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Impostazioni">
      <div className="max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-4 lg:gap-6 mb-8 border-b">
          {[
            { id: 'llm', label: 'LLM', icon: '🤖' },
            { id: 'integrations', label: 'Integrazioni', icon: '🔗' },
            { id: 'database', label: 'Database', icon: '💾' },
            { id: 'privacy', label: 'Privacy', icon: '🔒' },
            { id: 'users', label: 'Utenti', icon: '👥' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`px-4 lg:px-6 py-4 lg:py-5 text-base lg:text-lg font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 lg:mb-8 p-4 lg:p-5 rounded-2xl text-base lg:text-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-16">
            <p className="text-lg lg:text-xl text-gray-500">Caricamento...</p>
          </div>
        ) : (
          <>
            {/* LLM Tab */}
            {activeTab === 'llm' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Status card */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Stato LLM</h3>
                      <p className="text-base lg:text-lg text-gray-500 mt-1">
                        Provider: {provider}
                      </p>
                    </div>
                    <div
                      className={`px-4 py-2 rounded-xl text-base lg:text-lg font-medium ${
                        llmStatus === 'connected' || llmStatus === 'configured'
                          ? 'bg-green-100 text-green-700'
                          : llmStatus === 'checking'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {llmStatus === 'connected'
                        ? '● Connesso'
                        : llmStatus === 'configured'
                        ? '● Configurato'
                        : llmStatus === 'checking'
                        ? '○ Verifica...'
                        : '○ Non connesso'}
                    </div>
                  </div>
                </div>

                {/* Provider selection */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-3">Provider LLM</h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-6">
                    Scegli il modello di intelligenza artificiale da utilizzare
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-6">
                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Provider
                      </label>
                      <select
                        value={provider}
                        onChange={(e) => {
                          const newProvider = e.target.value;
                          setProvider(newProvider);
                          // Reset model to default for the new provider
                          if (newProvider === 'anthropic') {
                            setModel('claude-sonnet-4-5-20250929');
                          } else if (newProvider === 'openai') {
                            setModel('gpt-4o');
                          } else if (newProvider === 'ollama' && ollamaModels.length > 0) {
                            setModel(ollamaModels[0].name);
                          } else {
                            setModel('');
                          }
                        }}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="ollama">Ollama (Locale)</option>
                        <option value="anthropic">Anthropic (Claude)</option>
                        <option value="openai">OpenAI (GPT)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Modello
                      </label>
                      {provider === 'ollama' && ollamaModels.length > 0 ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {ollamaModels.map((m) => (
                            <option key={m.name} value={m.name}>
                              {m.name} ({m.size})
                            </option>
                          ))}
                        </select>
                      ) : provider === 'anthropic' ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</option>
                          <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                          <option value="claude-opus-4-6">Claude Opus 4.6</option>
                          <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
                        </select>
                      ) : provider === 'openai' ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          placeholder="mistral:latest"
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  </div>

                  {provider === 'ollama' && (
                    <div className="mb-4">
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        URL Ollama
                      </label>
                      <input
                        type="text"
                        value={ollamaUrl}
                        onChange={(e) => setOllamaUrl(e.target.value)}
                        placeholder="http://localhost:11434/v1"
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>

                {/* API Keys - Always visible */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-3">API Keys</h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-6">
                    Configura le chiavi API per i diversi provider cloud
                  </p>

                  <div className="space-y-5 lg:space-y-6">
                    {/* Anthropic API Key */}
                    <div className={`p-5 lg:p-6 rounded-2xl border ${provider === 'anthropic' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-base lg:text-lg font-medium text-gray-700">
                          Anthropic API Key (Claude)
                        </label>
                        <div className="flex items-center gap-2">
                          {anthropicKey ? (
                            <span className="text-sm lg:text-base bg-green-100 text-green-700 px-3 py-1 rounded-lg flex items-center gap-1">
                              <span className="text-green-500">●</span> Configurata
                            </span>
                          ) : (
                            <span className="text-sm lg:text-base bg-gray-100 text-gray-500 px-3 py-1 rounded-lg flex items-center gap-1">
                              <span>○</span> Non configurata
                            </span>
                          )}
                          {provider === 'anthropic' && (
                            <span className="text-sm lg:text-base bg-blue-600 text-white px-3 py-1 rounded-lg">Attivo</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="password"
                        value={anthropicKey}
                        onChange={(e) => setAnthropicKey(e.target.value)}
                        placeholder="sk-ant-api03-..."
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm lg:text-base text-gray-400 mt-2">
                        Ottieni la tua API key da{' '}
                        <a
                          href="https://console.anthropic.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          console.anthropic.com
                        </a>
                      </p>
                    </div>

                    {/* OpenAI API Key */}
                    <div className={`p-5 lg:p-6 rounded-2xl border ${provider === 'openai' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-base lg:text-lg font-medium text-gray-700">
                          OpenAI API Key (GPT)
                        </label>
                        <div className="flex items-center gap-2">
                          {openaiKey ? (
                            <span className="text-sm lg:text-base bg-green-100 text-green-700 px-3 py-1 rounded-lg flex items-center gap-1">
                              <span className="text-green-500">●</span> Configurata
                            </span>
                          ) : (
                            <span className="text-sm lg:text-base bg-gray-100 text-gray-500 px-3 py-1 rounded-lg flex items-center gap-1">
                              <span>○</span> Non configurata
                            </span>
                          )}
                          {provider === 'openai' && (
                            <span className="text-sm lg:text-base bg-blue-600 text-white px-3 py-1 rounded-lg">Attivo</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="password"
                        value={openaiKey}
                        onChange={(e) => setOpenaiKey(e.target.value)}
                        placeholder="sk-proj-..."
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm lg:text-base text-gray-400 mt-2">
                        Ottieni la tua API key da{' '}
                        <a
                          href="https://platform.openai.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          platform.openai.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Auto Route Images */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Routing Automatico Immagini</h3>
                      <p className="text-base lg:text-lg text-gray-500 mt-1">
                        Indirizza automaticamente le immagini ad Anthropic (Claude) che supporta la visione.
                      </p>
                      {autoRouteImages && !anthropicKey && (
                        <p className="text-base text-amber-600 mt-2">
                          ⚠️ Richiede una API key Anthropic configurata
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setAutoRouteImages(!autoRouteImages)}
                      className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        autoRouteImages ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          autoRouteImages ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Auto Route Documents */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Routing Automatico Documenti</h3>
                      <p className="text-base lg:text-lg text-gray-500 mt-1">
                        Indirizza automaticamente PDF e documenti ad Anthropic (workaround per instabilità GPU).
                      </p>
                      {autoRouteDocuments && !anthropicKey && (
                        <p className="text-base text-amber-600 mt-2">
                          ⚠️ Richiede una API key Anthropic configurata
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setAutoRouteDocuments(!autoRouteDocuments)}
                      className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        autoRouteDocuments ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          autoRouteDocuments ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 lg:px-8 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? 'Salvataggio...' : 'Salva Impostazioni'}
                  </button>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Strava Integration */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                      <span className="text-3xl">🚴</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Strava</h3>
                      <p className="text-base text-gray-500">Sincronizza allenamenti e attività</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Client ID
                      </label>
                      <input
                        type="text"
                        value={stravaClientId}
                        onChange={(e) => setStravaClientId(e.target.value)}
                        placeholder="12345"
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Client Secret
                      </label>
                      <input
                        type="password"
                        value={stravaClientSecret}
                        onChange={(e) => setStravaClientSecret(e.target.value)}
                        placeholder="abc123..."
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <h4 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Come ottenere le credenziali Strava
                    </h4>
                    <ol className="text-sm text-orange-700 space-y-2 ml-7 list-decimal">
                      <li>
                        Vai su{' '}
                        <a
                          href="https://www.strava.com/settings/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-orange-900 font-medium"
                        >
                          Strava API Settings
                        </a>
                      </li>
                      <li>Clicca su "Create App" o "Create an Application"</li>
                      <li>
                        Compila i campi richiesti:
                        <ul className="ml-4 mt-1 list-disc">
                          <li><strong>Application Name:</strong> Health Assistant</li>
                          <li><strong>Category:</strong> Health & Fitness</li>
                          <li><strong>Website:</strong> http://localhost:5173</li>
                          <li><strong>Authorization Callback Domain:</strong> localhost</li>
                        </ul>
                      </li>
                      <li>Copia il <strong>Client ID</strong> e <strong>Client Secret</strong> qui sopra</li>
                      <li>Salva le impostazioni e vai alla sezione <strong>Wearable → Integrazioni</strong> per collegare l'account</li>
                    </ol>
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 lg:px-8 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? 'Salvataggio...' : 'Salva Impostazioni'}
                  </button>
                </div>
              </div>
            )}

            {/* Database Tab */}
            {activeTab === 'database' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Database Stats */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-6 flex items-center gap-2">
                    <span>Statistiche Database</span>
                  </h3>

                  {dbStats ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                      <div className="bg-blue-50 rounded-2xl p-5 lg:p-6 text-center">
                        <p className="text-2xl lg:text-3xl font-bold text-blue-600">{dbStats.size}</p>
                        <p className="text-base lg:text-lg text-gray-600 mt-1">Dimensione</p>
                      </div>
                      <div className="bg-green-50 rounded-2xl p-5 lg:p-6 text-center">
                        <p className="text-2xl lg:text-3xl font-bold text-green-600">{dbStats.tables}</p>
                        <p className="text-base lg:text-lg text-gray-600 mt-1">Tabelle</p>
                      </div>
                      <div className="bg-purple-50 rounded-2xl p-5 lg:p-6 text-center">
                        <p className="text-lg lg:text-xl font-mono text-purple-600">{dbStats.name}</p>
                        <p className="text-base lg:text-lg text-gray-600 mt-1">Database</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-lg lg:text-xl text-gray-500">Caricamento statistiche...</p>
                  )}
                </div>

                {/* Backup & Restore */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-2">Backup & Restore</h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-6">
                    Gestisci i backup del tuo fascicolo sanitario
                  </p>

                  {/* Backup Path */}
                  <div className="mb-6">
                    <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                      Percorso Backup
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={backupPath}
                        onChange={(e) => setBackupPath(e.target.value)}
                        placeholder="./data/backups"
                        className="flex-1 px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => openFileBrowser()}
                        className="px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-600 text-xl"
                        title="Sfoglia"
                      >
                        📁
                      </button>
                    </div>
                    <p className="text-sm lg:text-base text-gray-400 mt-2">
                      Directory sul server dove salvare i backup
                    </p>
                  </div>

                  {/* Backup Actions */}
                  <div className="flex gap-4 mb-8">
                    <button
                      onClick={handleCreateBackup}
                      disabled={isBackingUp || isRestoring}
                      className="flex items-center gap-2 px-5 lg:px-6 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isBackingUp ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Backup in corso...
                        </>
                      ) : (
                        <>
                          <span>⬇️</span>
                          Crea Backup
                        </>
                      )}
                    </button>
                    <button
                      onClick={openRestoreModal}
                      disabled={isBackingUp || isRestoring}
                      className="flex items-center gap-2 px-5 lg:px-6 py-3 text-base lg:text-lg border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span>⬆️</span>
                      Ripristina da File
                    </button>
                  </div>

                  {/* Existing Backups */}
                  <div>
                    <h4 className="text-base lg:text-lg font-medium text-gray-700 mb-4 flex items-center gap-2">
                      <span>Backup Esistenti ({backups.length})</span>
                    </h4>

                    {backups.length === 0 ? (
                      <p className="text-base lg:text-lg text-gray-500 italic">Nessun backup trovato</p>
                    ) : (
                      <div className="space-y-3">
                        {backups.map((backup) => (
                          <div
                            key={backup.name}
                            className="flex items-center justify-between p-4 lg:p-5 bg-gray-50 rounded-xl"
                          >
                            <div>
                              <p className="font-semibold text-base lg:text-lg text-gray-900">{backup.name}</p>
                              <p className="text-sm lg:text-base text-gray-500">
                                {formatDate(backup.createdAt)} - {backup.size}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRestoreBackup(backup)}
                                disabled={isRestoring || isBackingUp}
                                title="Ripristina"
                                className="p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl disabled:opacity-50 text-xl"
                              >
                                ⬆️
                              </button>
                              <button
                                onClick={() => handleDeleteBackup(backup)}
                                disabled={isRestoring || isBackingUp}
                                title="Elimina"
                                className="p-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl disabled:opacity-50 text-xl"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Connection Info */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-4">Connessione</h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-4">
                    La connessione al database è configurata tramite variabili d'ambiente.
                  </p>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <code className="text-base lg:text-lg text-gray-600">
                      DATABASE_URL=postgresql://...
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-4">Privacy</h3>
                <p className="text-base lg:text-lg text-gray-500 mb-6">
                  I tuoi dati sanitari sono conservati localmente sul tuo server.
                </p>
                <ul className="space-y-3 text-base lg:text-lg text-gray-600">
                  <li className="flex items-center gap-3">
                    <span className="text-green-600">✓</span>
                    Nessun dato inviato a terze parti
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-green-600">✓</span>
                    Tutti i dati rimangono sul tuo server
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-green-600">✓</span>
                    Connessioni LLM criptate (HTTPS)
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-green-600">✓</span>
                    Con Ollama, anche l'AI è completamente locale
                  </li>
                </ul>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Gestione Utenti</h3>
                      <p className="text-base lg:text-lg text-gray-500 mt-1">
                        Visualizza tutti gli utenti registrati e le loro statistiche
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {usersData.filter(u => u.status === 'PENDING').length > 0 && (
                        <div className="px-4 py-2 rounded-xl text-base lg:text-lg font-medium bg-amber-100 text-amber-700">
                          ⏳ {usersData.filter(u => u.status === 'PENDING').length} in attesa
                        </div>
                      )}
                      <div className="px-4 py-2 rounded-xl text-base lg:text-lg font-medium bg-blue-100 text-blue-700">
                        {usersData.filter(u => u.status === 'ACTIVE').length} attivi
                      </div>
                    </div>
                  </div>
                </div>

                {isLoadingUsers ? (
                  <div className="text-center py-16">
                    <p className="text-lg lg:text-xl text-gray-500">Caricamento utenti...</p>
                  </div>
                ) : usersData.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-lg lg:text-xl text-gray-500">Nessun utente trovato</p>
                  </div>
                ) : (
                  <>
                    {/* Pending Users Section */}
                    {usersData.filter(u => u.status === 'PENDING').length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 lg:p-8">
                        <h4 className="font-semibold text-lg lg:text-xl text-amber-800 mb-4 flex items-center gap-2">
                          <span>⏳</span>
                          Utenti in Attesa di Approvazione ({usersData.filter(u => u.status === 'PENDING').length})
                        </h4>
                        <div className="space-y-4">
                          {usersData.filter(u => u.status === 'PENDING').map((userData) => (
                            <div key={userData.id} className="bg-white rounded-xl p-5 shadow-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-xl">
                                    ⏳
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-base lg:text-lg text-gray-900">
                                      {userData.name || userData.email.split('@')[0]}
                                    </h5>
                                    <p className="text-sm text-gray-500">{userData.email}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      Registrato il {new Date(userData.createdAt).toLocaleDateString('it-IT')} alle {new Date(userData.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleApproveUser(userData.id, userData.name || userData.email)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                                  >
                                    <span>✓</span>
                                    Approva
                                  </button>
                                  <button
                                    onClick={() => handleRejectUser(userData.id, userData.name || userData.email)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors"
                                  >
                                    <span>✗</span>
                                    Rifiuta
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Active Users Section */}
                    {usersData.filter(u => u.status === 'ACTIVE').length > 0 && (
                      <div>
                        <h4 className="font-semibold text-lg lg:text-xl text-gray-700 mb-4 flex items-center gap-2">
                          <span>👥</span>
                          Utenti Attivi ({usersData.filter(u => u.status === 'ACTIVE').length})
                        </h4>
                        <div className="space-y-4">
                          {usersData.filter(u => u.status === 'ACTIVE').map((userData) => (
                            <div key={userData.id} className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                              {/* User Header */}
                              <div className="flex items-start justify-between mb-6">
                                <div className="flex items-center gap-4">
                                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center text-2xl">
                                    {userData.role === 'ADMIN' ? '👑' : '👤'}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-lg lg:text-xl text-gray-900 flex items-center gap-2">
                                      {userData.name || userData.email.split('@')[0]}
                                      {userData.forcePasswordChange && (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                          Cambio password richiesto
                                        </span>
                                      )}
                                    </h4>
                                    <p className="text-base text-gray-500">{userData.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-3 py-1 rounded-lg text-sm lg:text-base font-medium ${
                                    userData.role === 'ADMIN'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {userData.role === 'ADMIN' ? 'Amministratore' : 'Membro'}
                                  </span>
                                </div>
                              </div>

                              {/* User Info Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                                <div>
                                  <p className="text-sm text-gray-500">Registrato</p>
                                  <p className="text-base font-medium text-gray-900">
                                    {new Date(userData.createdAt).toLocaleDateString('it-IT')}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Ultimo accesso</p>
                                  <p className="text-base font-medium text-gray-900">
                                    {userData.lastLoginAt
                                      ? new Date(userData.lastLoginAt).toLocaleDateString('it-IT')
                                      : 'Mai'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Provider LLM</p>
                                  <p className="text-base font-medium text-gray-900 capitalize">
                                    {userData.llmConfig.provider}
                                    {userData.llmConfig.hasCustomConfig && (
                                      <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                                        Personalizzato
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Modello</p>
                                  <p className="text-base font-medium text-gray-900 truncate" title={userData.llmConfig.model}>
                                    {userData.llmConfig.model}
                                  </p>
                                </div>
                              </div>

                              {/* Stats Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 rounded-xl p-4 text-center">
                                  <p className="text-2xl lg:text-3xl font-bold text-blue-600">
                                    {userData.stats.documentsCount}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">Documenti</p>
                                </div>
                                <div className="bg-green-50 rounded-xl p-4 text-center">
                                  <p className="text-2xl lg:text-3xl font-bold text-green-600">
                                    {userData.stats.conversationsCount}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">Conversazioni</p>
                                </div>
                                <div className="bg-purple-50 rounded-xl p-4 text-center">
                                  <p className="text-2xl lg:text-3xl font-bold text-purple-600">
                                    {userData.stats.labParametersCount}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">Parametri Lab</p>
                                </div>
                                <div className="bg-orange-50 rounded-xl p-4 text-center">
                                  <p className="text-2xl lg:text-3xl font-bold text-orange-600">
                                    {userData.stats.diskSpaceFormatted}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">Spazio Disco</p>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                                <button
                                  onClick={() => handleForcePasswordChange(userData.id, userData.name || userData.email)}
                                  disabled={userData.forcePasswordChange}
                                  className={`flex items-center gap-2 px-4 py-2 text-sm rounded-xl transition-colors ${
                                    userData.forcePasswordChange
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                  }`}
                                >
                                  <span>🔑</span>
                                  {userData.forcePasswordChange ? 'Cambio già richiesto' : 'Forza cambio password'}
                                </button>

                                {userData.email !== 'admin@example.com' && (
                                  <button
                                    onClick={() => handleDeleteUser(userData.id, userData.name || userData.email)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors"
                                  >
                                    <span>🗑️</span>
                                    Elimina utente
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rejected Users Section */}
                    {usersData.filter(u => u.status === 'REJECTED').length > 0 && (
                      <div>
                        <h4 className="font-semibold text-lg lg:text-xl text-gray-400 mb-4 flex items-center gap-2">
                          <span>🚫</span>
                          Utenti Rifiutati ({usersData.filter(u => u.status === 'REJECTED').length})
                        </h4>
                        <div className="space-y-4">
                          {usersData.filter(u => u.status === 'REJECTED').map((userData) => (
                            <div key={userData.id} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center text-xl text-gray-400">
                                    🚫
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-base lg:text-lg text-gray-500">
                                      {userData.name || userData.email.split('@')[0]}
                                    </h5>
                                    <p className="text-sm text-gray-400">{userData.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleApproveUser(userData.id, userData.name || userData.email)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-colors"
                                  >
                                    <span>✓</span>
                                    Riattiva
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(userData.id, userData.name || userData.email)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors"
                                  >
                                    <span>🗑️</span>
                                    Elimina
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Filesystem Browser Modal */}
      {showFileBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-5 lg:p-6 border-b flex justify-between items-center">
              <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Seleziona Directory</h3>
              <button
                onClick={() => setShowFileBrowser(false)}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 lg:p-6">
              {/* Current path */}
              <div className="mb-4 p-3 bg-gray-100 rounded-xl text-base lg:text-lg font-mono truncate">
                {browserPath}
              </div>

              {/* Navigation */}
              <div className="mb-4">
                <button
                  onClick={() => navigateToDir(browserParent)}
                  disabled={browserPath === browserParent}
                  className="text-base lg:text-lg text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  ⬆️ Directory superiore
                </button>
              </div>

              {/* Directory list */}
              <div className="border rounded-xl max-h-64 overflow-y-auto">
                {browserDirs.length === 0 ? (
                  <p className="p-4 text-base lg:text-lg text-gray-500 italic">Nessuna sottodirectory</p>
                ) : (
                  browserDirs.map((dir) => (
                    <button
                      key={dir.path}
                      onClick={() => navigateToDir(dir.path)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3 text-base lg:text-lg"
                    >
                      <span>📁</span>
                      <span className="truncate">{dir.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="p-5 lg:p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowFileBrowser(false)}
                className="px-5 lg:px-6 py-3 text-base lg:text-lg border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={selectBrowserPath}
                className="px-5 lg:px-6 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                Seleziona questa directory
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore from File Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-5 lg:p-6 border-b flex justify-between items-center">
              <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Ripristina da File</h3>
              <button
                onClick={() => setShowRestoreModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 lg:p-6">
              <p className="text-base lg:text-lg text-gray-500 mb-5">
                Inserisci il percorso completo del file di backup da ripristinare.
              </p>

              <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                Percorso File Backup
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={restoreFilePath}
                  onChange={(e) => setRestoreFilePath(e.target.value)}
                  placeholder="/path/to/backup.sql"
                  className="flex-1 px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    openFileBrowser(restoreFilePath || backupPath, 'restore');
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-600 text-xl"
                  title="Sfoglia"
                >
                  📁
                </button>
              </div>
              <p className="text-sm lg:text-base text-gray-400 mt-2">
                Seleziona un file .sql dal server
              </p>

              <div className="mt-5 p-4 lg:p-5 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-base lg:text-lg text-yellow-800">
                  ⚠️ <strong>Attenzione:</strong> Il ripristino sovrascriverà i dati attuali del database.
                </p>
              </div>
            </div>

            <div className="p-5 lg:p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="px-5 lg:px-6 py-3 text-base lg:text-lg border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleRestoreFromFile}
                disabled={!restoreFilePath}
                className="px-5 lg:px-6 py-3 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Ripristina
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
